import { useState } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { GooglePlacesAutocomplete } from '../ui/google-places-autocomplete';
import { WheelTimePicker } from './WheelTimePicker';
import { toast } from 'sonner';
import { Plus, Loader2 } from 'lucide-react';

/**
 * Lets a field worker add a route stop (waypoint/pickup/dropoff) to a haulage job.
 * Supports inserting between existing stops.
 */
export const AddRouteStopDialog = ({ jobId, stops = [], api, onUpdated, country = 'AU' }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [type, setType] = useState('waypoint');
  const [position, setPosition] = useState('end'); // 'end' or an order number (string)
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [estArrival, setEstArrival] = useState('');
  const [address, setAddress] = useState({ address: '', suburb: '', state: '', postcode: '' });

  const reset = () => {
    setType('waypoint');
    setPosition('end');
    setLabel('');
    setNotes('');
    setEstArrival('');
    setAddress({ address: '', suburb: '', state: '', postcode: '' });
  };

  const handlePlaceSelect = (place) => {
    setAddress({
      address: place.street_address || place.formatted_address || '',
      suburb: place.suburb || '',
      state: place.state || '',
      postcode: place.postcode || '',
    });
  };

  const handleSubmit = async () => {
    if (!address.address && !address.suburb && !label.trim()) {
      toast.error('Add a location name or an address');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        type,
        label: label.trim() || null,
        address: address.address,
        suburb: address.suburb,
        state: address.state,
        postcode: address.postcode,
        notes: notes || null,
        est_arrival: estArrival || null,
        insert_after_order: position === 'end' ? null : parseInt(position, 10),
      };
      const res = await api.post(`/api/jobs/${jobId}/stops`, payload);
      onUpdated(res.data.stops, res.data.route_summary);
      toast.success('Stop added');
      reset();
      setOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add stop');
    } finally {
      setSaving(false);
    }
  };

  const sortedStops = [...stops].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="w-full border-dashed border-[var(--m-border-light)] text-[#ffd108] hover:bg-[#ffd108]/10"
        onClick={() => setOpen(true)}
        data-testid="add-waypoint-btn"
      >
        <Plus className="h-4 w-4 mr-1" /> Add waypoint
      </Button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] max-w-md max-h-[90vh] overflow-y-auto" data-testid="add-waypoint-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--m-text)]">Add Route Stop</DialogTitle>
            <DialogDescription className="text-[var(--m-text-secondary)] text-xs">Add a pickup, dropoff or waypoint to this route.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Stop type</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]" data-testid="add-waypoint-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]">
                  <SelectItem value="pickup">Pickup</SelectItem>
                  <SelectItem value="dropoff">Dropoff</SelectItem>
                  <SelectItem value="waypoint">Waypoint</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Location name / title</label>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. North Depot (optional if address given)"
                className="w-full h-10 px-3 rounded-md bg-[var(--m-bg)] border border-[var(--m-border-light)] text-[var(--m-text)] text-sm placeholder:text-[var(--m-text-muted)] focus:outline-none focus:ring-1 focus:ring-[#ffd108]"
                data-testid="add-waypoint-label"
              />
            </div>

            <div>
              <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Address {label.trim() ? '(optional)' : ''}</label>
              <GooglePlacesAutocomplete
                onPlaceSelect={handlePlaceSelect}
                placeholder="Search for an address..."
                country={country}
                className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
                data-testid="add-waypoint-address"
              />
              {address.suburb && (
                <p className="text-[var(--m-text-secondary)] text-xs mt-1">{[address.address, address.suburb, address.state, address.postcode].filter(Boolean).join(', ')}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Position</label>
                <Select value={position} onValueChange={setPosition}>
                  <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]" data-testid="add-waypoint-position">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]">
                    <SelectItem value="end">At the end</SelectItem>
                    {sortedStops.map((s) => (
                      <SelectItem key={s.id} value={String(s.order)}>
                        After {s.label || s.suburb || s.address || `Stop ${s.order}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">ETA (optional)</label>
                <WheelTimePicker
                  value={estArrival}
                  onChange={setEstArrival}
                  minuteStep={5}
                  className="w-full h-10 bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]"
                  testId="add-waypoint-eta"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-[var(--m-text-secondary)] mb-1 block">Notes (optional)</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any details for this stop..."
                className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] resize-none"
                rows={2}
                data-testid="add-waypoint-notes"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" className="border-[var(--m-border-light)] text-[var(--m-text-body)]" onClick={() => { setOpen(false); reset(); }} disabled={saving}>
              Cancel
            </Button>
            <Button className="bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90" onClick={handleSubmit} disabled={saving} data-testid="add-waypoint-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Stop'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddRouteStopDialog;
